import { useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { usePlatformFeatureFlags } from '@components/utils/DashboardFeatureFlags/usePlatformFeatureFlags'
import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import { useModalContext } from '@fourthwall/components'
import type { Offer } from '@fourthwall/services/dist/models/offer'
import { useFormikContext } from '@fourthwall/utils/dist/formik'
import { SHIPPING_MODAL_HASH } from '@modules/Dashboard/Product/Product.consts'
import { useHash } from '@utils/useHash/useHash'

import { useAdminModeContext } from '../../../../../../../App/providers/AdminModeProvider'
import { useOfferUpdateShippingTypeMutation } from '../../../../../../hooks/useOfferUpdateShippingTypeMutation'
import { useOffer } from '../../../../../hooks/useOffer'
import type { ProductFormState } from '../../../../SingleProduct.types'
import { getInitialValues } from '../../../../SingleProduct.utils'
import {
  SelfOrAssistedShippingChoiceModal,
  type ShippingChangeOption,
} from './SelfOrAssistedShippingChoiceModal'

const options: Array<ShippingChangeOption> = [
  { id: 'WEIGHT_BASED_SELF_SHIPPING' },
  { id: 'SHIPPING_3PL' },
]

export const useShippingChoiceModal = () => {
  const { hasHash, removeHash, addHash } = useHash()
  const { open, close } = useModalContext()
  const { productId = '' } = useParams<'productId'>()
  const { offer, getOfferFromOfferData, invalidateOffer } = useOffer(productId)
  const { hasFeatureFlag } = usePlatformFeatureFlags()
  const { hasPermission } = useDashboardPermissions()
  const adminMode = useAdminModeContext()
  const { updateOfferShippingTypeMutation } = useOfferUpdateShippingTypeMutation()
  const { values, setValues, resetForm } = useFormikContext<ProductFormState>()

  const openShippingModal = () => addHash(SHIPPING_MODAL_HASH)

  useEffect(() => {
    if (
      hasHash(SHIPPING_MODAL_HASH) &&
      ((hasFeatureFlag('assisted_product_shipping') &&
        hasPermission('products.thirdPartyLogistics.edit')) ||
        Boolean(adminMode?.isActive))
    ) {
      open(SelfOrAssistedShippingChoiceModal, {
        initialOption: offer.config?.shippingType === 'SHIPPING_3PL' ? options[1] : options[0],
        options,
        async onConfirm(option) {
          await updateOfferShippingTypeMutation.mutateAsync(
            [{ offerId: offer.id! }, { shippingType: option.id }],
            {
              onSuccess(data) {
                invalidateOffer()

                const newValues = getInitialValues({
                  // NOTE: updateOfferShippingTypeMutation response has different type and can be partial
                  offer: getOfferFromOfferData({ ...offer, ...data } as Offer.Item),
                })

                resetForm({
                  values: newValues,
                })
                setValues({
                  ...values,
                  fulfillmentService: newValues.fulfillmentService,
                  isCustomsInformationEnabled: newValues.isCustomsInformationEnabled,
                })
                removeHash()
                close()
              },
            },
          )
        },
        onClose() {
          removeHash()
          close()
        },
        onRequestClose() {
          removeHash()
          close()
        },
      })
    }
  }, [
    close,
    hasFeatureFlag,
    hasHash,
    offer.config?.shippingType,
    offer.id,
    open,
    removeHash,
    setValues,
    resetForm,
    updateOfferShippingTypeMutation,
  ])

  return {
    openShippingModal,
  }
}
